import React from 'react';
import Header from '@/components/common/Header';
import SEOHead from '@/components/common/SEOHead';
import FooterSection from '@/components/homepage/FooterSection';
import { useAnalytics } from '@/hooks/useAnalytics';

const ReferralTerms = () => {
  const { trackNavigation } = useAnalytics(); // This also handles page view tracking
  
  return (
    <div className="min-h-screen bg-[#F7F5F2] font-nunito">
      <SEOHead pageKey="referral-terms" />
      <Header showActions={true} />
      
      <main className="pt-[80px]">
        {/* Header */}
        <div className="section-container py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-[32px] md:text-[72px] font-bold text-[#6C4F77] mb-4 leading-tight">Referral Program Terms & Conditions</h1>
            <p className="text-[#2C2C2C] text-lg">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white py-12 mx-[20px] md:mx-0 rounded-[8px] md:rounded-none">
          <div className="section-container">
            <div className="max-w-4xl mx-auto space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">1. Eligibility & Participation</h2>
                <p className="text-[#2C2C2C] leading-relaxed mb-4">
                  Open to all current WabiSabi users aged 18 and over.
                </p>
                <p className="text-[#2C2C2C] leading-relaxed">
                  You cannot refer yourself, create multiple accounts, or use fake emails. Any suspicious activity may result in disqualification.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">2. How It Works</h2>
                <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                  <li>Share your unique referral code or link with friends.</li>
                  <li>When a referred friend signs up with your code and completes verification, they count toward your reward.</li>
                  <li>Rewards are granted once 5 verified signups are completed.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">3. Reward Details</h2>
                <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                  <li>Earn a €20 gift card, delivered via email, after each set of 5 valid referrals.</li>
                  <li>Gift cards are issued in Euros by default.</li>
                  <li>Upon request, we can issue gift cards in other currencies (USD, GBP, etc.). Currency conversion is based on current exchange rates.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">4. Reward Distribution</h2>
                <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                  <li>Gift cards are sent directly to the email associated with your Wabisabi account.</li>
                  <li>Your rewards will only be issued after all referred emails are successfully verified by our system.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">5. Limits & Restrictions</h2>
                <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                  <li>Rewards are not transferable, refundable for cash, and cannot be exchanged for other items.</li>
                  <li>No limit to how many €20 rewards you can earn, but each referral must be a complete set of 5 verified users.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">6. Fraud & Abuse</h2>
                <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                  <li>A referral is invalid if it is created fraudulently e.g., spam, self-referral, bots, duplicates.</li>
                  <li>Wabisabi reserves the right to void any suspicious or invalid referrals, in its sole discretion.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">7. Program Changes & Termination</h2>
                <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                  <li>Wabisabi may modify, suspend, or terminate the referral program or these terms at any time without prior notice.</li>
                  <li>Updated terms will take effect immediately upon posting. Continuing to participate constitutes acceptance of changes.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">8. Privacy & Compliance</h2>
                <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                  <li>We respect your privacy. Participant info is handled per our Privacy Policy.</li>
                  <li>By referring friends, you confirm you have their consent to share their email addresses.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">9. General</h2>
                <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                  <li>These terms are governed by the laws of your jurisdiction.</li>
                  <li>Wabisabi reserves the right to make final decisions regarding eligibility, rewards, and disputes.</li>
                </ul>
              </section>

              <section className="border-t pt-6">
                <p className="text-sm text-[#2C2C2C]/60 text-center">
                  By participating in the Wabisabi Referral Program, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
      <FooterSection />
    </div>
  );
};

export default ReferralTerms;